import React from 'react';
import { Calendar, Building2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Campaign, calculateCampaignProgress } from '@/hooks/useCampaigns';
import { format, parseISO, isValid, differenceInCalendarDays } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/contexts/OrganizationContext';
import { formatCurrency } from '@/utils/currency';

export interface CampaignTableViewProps {
  campaigns: Campaign[];
}

const calculateDurationInDays = (startDateStr?: string | null, endDateStr?: string | null): string => {
  if (!startDateStr || !endDateStr) return '';
  const start = parseISO(startDateStr);
  const end = parseISO(endDateStr);
  if (!isValid(start) || !isValid(end)) return '';

  const days = differenceInCalendarDays(end, start) + 1;
  return days > 0 ? `${days} days` : '';
};

const formatDateRange = (startDateStr?: string | null, endDateStr?: string | null): string => {
  if (!startDateStr && !endDateStr) return 'Dates pending';

  let startFormatted = '';
  let endFormatted = '';

  if (startDateStr) {
    const s = parseISO(startDateStr);
    if (isValid(s)) startFormatted = format(s, 'MMM d, yyyy');
  }

  if (endDateStr) {
    const e = parseISO(endDateStr);
    if (isValid(e)) endFormatted = format(e, 'MMM d, yyyy');
  }

  if (startFormatted && endFormatted) {
    // If same year, shorten start format e.g. "Jul 31 - Aug 31, 2026"
    const sYear = startDateStr?.split('-')[0];
    const eYear = endDateStr?.split('-')[0];
    if (sYear === eYear && startDateStr) {
      const s = parseISO(startDateStr);
      if (isValid(s)) startFormatted = format(s, 'MMM d');
    }
    return `${startFormatted} - ${endFormatted}`;
  } else if (startFormatted) {
    return `From ${startFormatted}`;
  } else if (endFormatted) {
    return `Until ${endFormatted}`;
  }
  return 'Dates pending';
};

export const CampaignTableView: React.FC<CampaignTableViewProps> = ({ campaigns }) => {
  const navigate = useNavigate();
  const { userCountry } = useAuth();
  const { currentOrganization } = useOrganization();
  const isBrandOrg = currentOrganization?.organization_type === 'brand';

  return (
    <div className="bg-white rounded-xl border border-gray-200/80 shadow-sm overflow-hidden mb-3">
      <div className="overflow-x-auto">
        <table className="w-full text-left border-collapse">
          <thead>
            <tr className="border-b border-gray-100 bg-gray-50/50 text-[11px] font-semibold text-gray-500 tracking-wider uppercase">
              <th className="py-3.5 px-6">CAMPAIGN</th>
              {!isBrandOrg && <th className="py-3.5 px-4">BRAND</th>}
              <th className="py-3.5 px-4">STATUS</th>
              <th className="py-3.5 px-4">BUDGET</th>
              <th className="py-3.5 px-4">PERIOD</th>
              <th className="py-3.5 px-4">OWNER</th>
              <th className="py-3.5 px-6">PROGRESS</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 text-sm">
            {campaigns.map((campaign) => {
              // Ensure progress calculation matches date ratio accurately (e.g., 56%)
              const progressPct = campaign.progress_percent ?? calculateCampaignProgress(campaign.start_date, campaign.end_date, campaign.status);
              const statusClean = (campaign.status || 'draft').toLowerCase();
              const dateRangeText = formatDateRange(campaign.start_date, campaign.end_date);
              const durationText = calculateDurationInDays(campaign.start_date, campaign.end_date);

              // Filter out raw UUID brand strings to guarantee clean brand name display
              const isBrandUuid = campaign.brand && /^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(campaign.brand);
              let displayBrandName = isBrandUuid ? (campaign.brand_name || 'Unassigned') : (campaign.brand || 'Unassigned');
              if ((!displayBrandName || displayBrandName === 'Unassigned') && isBrandOrg && currentOrganization?.name) {
                displayBrandName = currentOrganization.name;
              }

              return (
                <tr
                  key={campaign.id}
                  onClick={() => navigate(`/campaigns/${campaign.id}`)}
                  className="hover:bg-purple-50/30 transition-colors cursor-pointer group"
                >
                  {/* CAMPAIGN */}
                  <td className="py-4 px-6">
                    <div className="font-semibold text-gray-900 group-hover:text-purple-600 transition-colors">
                      {campaign.name}
                    </div>
                  </td>

                  {/* BRAND */}
                  {!isBrandOrg && (
                    <td className="py-4 px-4 whitespace-nowrap">
                      <div className="flex items-center space-x-2.5">
                        {campaign.brand_logo ? (
                          <img
                            src={campaign.brand_logo}
                            alt={displayBrandName}
                            className="w-[2.625rem] h-[2.625rem] rounded-lg border border-gray-200 object-cover shrink-0"
                          />
                        ) : (
                          <div className="w-[2.625rem] h-[2.625rem] rounded-lg bg-black text-white flex items-center justify-center text-sm font-bold shrink-0">
                            {displayBrandName && displayBrandName !== 'Unassigned'
                              ? displayBrandName[0].toUpperCase()
                              : <Building2 className="w-5 h-5" />}
                          </div>
                        )}
                        <span className="font-medium text-gray-800">{displayBrandName}</span>
                      </div>
                    </td>
                  )}

                  {/* STATUS */}
                  <td className="py-4 px-4 whitespace-nowrap">
                    {statusClean === 'active' && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-blue-50 text-blue-600 border border-blue-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-blue-500 mr-1.5"></span>
                        Active
                      </span>
                    )}
                    {statusClean === 'completed' && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-emerald-50 text-emerald-700 border border-emerald-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 mr-1.5"></span>
                        Completed
                      </span>
                    )}
                    {statusClean === 'draft' && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500 mr-1.5"></span>
                        Draft
                      </span>
                    )}
                    {statusClean !== 'active' && statusClean !== 'completed' && statusClean !== 'draft' && (
                      <span className="inline-flex items-center px-2.5 py-1 rounded-full text-xs font-medium bg-gray-100 text-gray-700 border border-gray-200">
                        <span className="w-1.5 h-1.5 rounded-full bg-gray-500 mr-1.5"></span>
                        {campaign.status}
                      </span>
                    )}
                  </td>

                  {/* BUDGET */}
                  <td className="py-4 px-4 whitespace-nowrap font-medium text-gray-900">
                    {formatCurrency(campaign.total_budget || campaign.budget, userCountry, {
                      minimumFractionDigits: 0,
                      maximumFractionDigits: 0,
                    })}
                  </td>

                  {/* PERIOD */}
                  <td className="py-4 px-4 whitespace-nowrap">
                    <div className="flex items-center space-x-1.5 text-xs text-gray-700 font-medium">
                      <Calendar className="w-3.5 h-3.5 text-gray-400 shrink-0" />
                      <span>{dateRangeText}</span>
                    </div>
                    {durationText && (
                      <div className="text-[11px] text-gray-400 pl-5 mt-0.5">{durationText}</div>
                    )}
                  </td>

                  {/* OWNER */}
                  <td className="py-4 px-4 whitespace-nowrap">
                    <span className="text-xs font-medium text-gray-800">
                      {campaign.owner_name || 'Unassigned'}
                    </span>
                  </td>

                  {/* PROGRESS */}
                  <td className="py-4 px-6 whitespace-nowrap min-w-[140px]">
                    <div className="flex items-center justify-between text-xs font-semibold text-gray-700 mb-1">
                      <span>{progressPct}%</span>
                    </div>
                    <div className="w-full bg-gray-100 rounded-full h-1.5 overflow-hidden">
                      <div
                        className={`h-full rounded-full transition-all duration-500 ${progressPct === 100
                            ? 'bg-emerald-500'
                            : progressPct > 0
                              ? 'bg-[#A655F8]'
                              : 'bg-transparent'
                          }`}
                        style={{ width: `${progressPct}%` }}
                      ></div>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
};
